import { ClaudeMessage, Conversation } from '~api';
import { Vals } from '~lib';

/**
 * @desc What the agent is doing, in the user's language. The vocabulary is deliberately small
 * and closed: the indicator is a reassurance that something is happening, not a trace, and a
 * label that leaked a tool name (`python_executor`) or an internal phase (`slot verification`)
 * would be worse than saying nothing.
 */
export const ActivityLabels = {
  Preparing: 'Preparing',
  ReadingData: 'Reading the data',
  QueryingData: 'Querying the data',
  WritingCode: 'Writing code',
  BuildingChart: 'Building a chart',
  BuildingReport: 'Building a report',
  PreparingDownload: 'Preparing a download',
  CheckingAnswer: 'Checking my answer',
  ThinkingAboutResults: 'Thinking about the results',
  WritingAnswer: 'Writing my answer',
  WrappingUp: 'Wrapping up',
  WarmingUp: 'Warming up the data connection',
  /** @desc A batch doing too many distinct things to name; see `labelForBatch`. */
  WorkingOnIt: 'Working on it',
} as const;

export type TActivityLabel = Vals<typeof ActivityLabels>;

/**
 * @desc What a `python_executor` call is for, read off the `description` the model is required
 * to write for every call — the only per-call signal there is. Ordered by precedence, most
 * specific first: "verify the chart data" is fact-checking, not chart-building, so the verify
 * pattern has to be consulted before the chart one.
 */
const PYTHON_PATTERNS: Array<[RegExp, TActivityLabel]> = [
  [/verif|register.*fact|reconcil/i, ActivityLabels.CheckingAnswer],
  [/chart|plot|graph|visuali[sz]ation/i, ActivityLabels.BuildingChart],
  [/\bpdf\b|html report|\breport\b/i, ActivityLabels.BuildingReport],
  [/export|\bcsv\b|\bxlsx\b|download/i, ActivityLabels.PreparingDownload],
  [
    /query|select|clickhouse|compute|debug|check|explore|investigate|compare|isolate|get |build.*table/i,
    ActivityLabels.QueryingData,
  ],
];

/** @desc Skills whose whole purpose is a deliverable, keyed to what that deliverable is. */
const SKILL_LABELS: Array<[RegExp, TActivityLabel]> = [
  [/^chart-/i, ActivityLabels.BuildingChart],
  [
    /^(h2-html-reports|pdf-reports|table-presentation|figure-formatting)$/i,
    ActivityLabels.BuildingReport,
  ],
];

/** @desc Tools that only ever look at the shape of the warehouse. */
const READING_TOOLS = new Set(['list_tables', 'describe_table_schema']);

const stringField = (input: unknown, key: string): string | undefined => {
  if (!input || typeof input !== 'object' || !(key in input)) return undefined;
  const value = (input as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
};

/**
 * @desc The activity one tool call represents. Every unmapped tool — the dev-only stubs, a tool
 * added after this shipped — lands on `Writing code`: a safe generic that is never wrong enough
 * to mislead, and never specific enough to expose internals.
 */
export function classifyToolCall(call: ClaudeMessage.C.ToolUse): TActivityLabel {
  if (READING_TOOLS.has(call.name)) return ActivityLabels.ReadingData;

  if (call.name === 'load_skill') {
    const name = stringField(call.input, 'skill') ?? '';
    const matched = SKILL_LABELS.find(([pattern]) => pattern.test(name));
    // A methodology or glossary skill is context-gathering, not a deliverable.
    return matched?.[1] ?? ActivityLabels.Preparing;
  }

  if (call.name === 'python_executor') {
    const description = stringField(call.input, 'description') ?? '';
    const matched = PYTHON_PATTERNS.find(([pattern]) => pattern.test(description));
    return matched?.[1] ?? ActivityLabels.WritingCode;
  }

  return ActivityLabels.WritingCode;
}

/** @desc "Building a chart" → "building a chart", for the tail of a joined pair. */
const decapitalise = (label: string) => `${label.charAt(0).toLowerCase()}${label.slice(1)}`;

/**
 * @desc One label for one batch of tool calls — the `tool_use` blocks of a single model
 * message, dispatched together and therefore experienced by the user as one step. Two distinct
 * activities read fine joined; three or more is a list, and a list is not a status line.
 */
export function labelForBatch(calls: ClaudeMessage.C.ToolUse[]): TActivityLabel | string | null {
  const distinct = [...new Set(calls.map(classifyToolCall))];
  const [first, second] = distinct;
  if (!first) return null;
  if (distinct.length === 1) return first;
  if (distinct.length === 2 && second) return `${first} and ${decapitalise(second)}`;
  return ActivityLabels.WorkingOnIt;
}

const toolUseBlocks = (payload: ClaudeMessage.All): ClaudeMessage.C.ToolUse[] => {
  const { content } = payload;
  if (typeof content === 'string') return [];
  return content.filter((block): block is ClaudeMessage.C.ToolUse => block.type === 'tool_use');
};

const hasText = (payload: ClaudeMessage.All): boolean => {
  const { content } = payload;
  if (typeof content === 'string') return content.length > 0;
  return content.some((block) => block.type === 'text');
};

/**
 * @desc The activity to show for a turn, computed from scratch over the turn's persisted
 * events. `null` means show nothing: the turn has not started producing yet, or it is over.
 *
 * Purity is the contract, not an implementation detail. The events are the same array whether
 * they arrived one at a time down the socket or in one lump from a reload, so a live turn and
 * a turn reloaded half-way through resolve to the same label — which they could not if the
 * deriver carried a "last shown" reference the log cannot reproduce.
 *
 * Note that two of the signals below are not on the client's projection today:
 * `agent-reducer.ts` puts neither `ToolsFinished` nor any `source: 'agent'` ClaudeMessage into
 * `contexts.user`, which is the whole of what the portal receives. They are handled anyway —
 * the spec's fixtures are written over the backend's full history, and the branches become
 * live the day the projection carries them, without a second reading of the same rules.
 */
export function deriveActivityLabel(
  events: Array<Conversation.ClientEvent<Date | string, string>>,
): TActivityLabel | string | null {
  let label: TActivityLabel | string | null = null;
  // Has any tool run yet this turn? Separates the first think ("Preparing") from every later
  // one ("Thinking about the results").
  let sawTools = false;
  // Inside a rejected-answer repair cycle: the model is fixing its own draft, so the think that
  // follows the synthetic repair prompt must not read as digesting fresh data.
  let repairing = false;

  for (const evt of events) {
    if (evt.name === 'ClaudeMessage') {
      if (evt.source === 'user') continue;

      if (evt.source === 'agent') {
        // The rejected draft itself; the repair prompt that follows carries no marker and is
        // left to the `repairing` flag.
        if (evt.metadata.answer?.verification === 'rejected') {
          repairing = true;
          label = ActivityLabels.CheckingAnswer;
        }
        continue;
      }

      const calls = toolUseBlocks(evt.payload);
      if (calls.length > 0) {
        repairing = false;
        sawTools = true;
        label = labelForBatch(calls) ?? label;
        continue;
      }

      if (hasText(evt.payload)) {
        // A model message with text and no tools is the answer — including the synthetic
        // abstention, which is presented as an answer like any other.
        repairing = false;
        label = ActivityLabels.WritingAnswer;
      }
      continue;
    }

    // A tool result changes nothing, failure included: the model is expected to read the error
    // and retry, and a red flash for a step the agent recovers from teaches the wrong lesson.
    if (evt.name === 'ToolOutput') continue;

    if (evt.name !== 'System') continue;

    switch (evt.payload.code) {
      case 'ThinkingStart':
        label = repairing
          ? ActivityLabels.CheckingAnswer
          : sawTools
            ? ActivityLabels.ThinkingAboutResults
            : ActivityLabels.Preparing;
        break;
      case 'WarmingUp':
        // ClickHouse is cold-starting, not the agent stalling — say so over whatever was showing.
        label = ActivityLabels.WarmingUp;
        break;
      case 'ToolsFinished':
        repairing = false;
        break;
      case 'Summarisation':
        label = ActivityLabels.WrappingUp;
        break;
      case 'ModelCallFailed':
      case 'TurnComplete':
        // The turn is over. A failure hands over to the error card, which is never stacked with
        // the indicator; a completion hands over to the answer.
        return null;
      default:
        // ConversationLengthAdvisory — a footnote to the answer, not an activity.
        break;
    }
  }

  return label;
}
